import { ChevronDownIcon } from 'lucide-react'
import React, { forwardRef } from 'react'

import { cn } from 'utils'
import { mergeRefs } from 'utils/ref_utils'

import { useFilledState } from './hooks/useFilledState'
import { SolidCircleCheckIcon } from './icons/SolidCircleCheck'
import {
  TextLikeFormField,
  type TextLikeFormFieldProps
} from './TextLikeFormField'

export interface SelectProps extends Omit<TextLikeFormFieldProps, 'children'>, React.SelectHTMLAttributes<HTMLSelectElement> {
  children: React.ReactNode;
  placeholder?: string;
  validated?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  children,
  placeholder,
  validated,
  ...props
}, forwardedRef) => {
  const { filled, handleChange, ref } = useFilledState(
    props.defaultValue,
    props.value,
    props.onChange
  )

  return (
    <TextLikeFormField {...props}>
      {(formFieldProps) => (
        <>
          <select
            {...formFieldProps}
            data-placeholder={!filled}
            className={cn(formFieldProps.className, 'truncate', {
              'pr-8': !validated,
              'pr-12': validated,
            })}
            onChange={handleChange}
            ref={mergeRefs(ref, forwardedRef)}
          >
            {!!placeholder && (
              <option disabled={props.required} value=''>
                {placeholder}
              </option>
            )}
            {children}
          </select>
          <div className='flex items-center gap-1 absolute right-2 top-0 bottom-0'>
            {validated && (
              <span className='pointer-events-none text-green-400'>
                <SolidCircleCheckIcon size={16} />
              </span>
            )}
            <span className='pointer-events-none text-gray-500 group-has-[:open]/field:rotate-180'>
              <ChevronDownIcon aria-hidden size={16} />
            </span>
          </div>
        </>
      )}
    </TextLikeFormField>
  )
})
